import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import { Icon } from '@hz-design-system/web-ui';

import { getThumbnailImageUrl } from '../../../../utils/getImageUrls';
import { TListingGallery } from 'types/TListing';

import Classes from './Thumbnails.module.scss';

type TThumbnailsProps = {
  imageUrls: TListingGallery['imageUrls'];
  imageSizes?: TListingGallery['media']['imageSizes'];
  activeImageIndex?: number;
  onChange: Function;
  imageFit: 'cover' | 'contain';
  loadAll?: boolean;
  videoUrl?: string;
};

const HALF = 2;

const Thumbnails = ({
  imageUrls,
  imageSizes,
  onChange,
  imageFit,
  activeImageIndex = 0,
  loadAll = false,
  videoUrl,
}: TThumbnailsProps) => {
  const { t } = useI18nContext();
  const [activeThumbnail, setActiveThumbnail] = useState(activeImageIndex);
  const [loadAllImages, setLoadAllImages] = useState(loadAll);
  const thumbnailScrollElement = useRef<HTMLHeadingElement>(null);
  const thumbnailContainerElement = useRef<HTMLHeadingElement>(null);
  const maxVisibleImageCount = 5;
  const videoOffset = videoUrl ? 1 : 0;
  const itemCount = imageUrls.length + videoOffset;

  const onClick = (thumbnailIndex: number) => {
    if (thumbnailIndex === activeThumbnail) {
      return;
    }
    setActiveThumbnail(thumbnailIndex);
    onChange(thumbnailIndex);
  };

  const showThumbnailInView = useCallback(
    (index: number) => {
      if (thumbnailScrollElement?.current && thumbnailContainerElement?.current) {
        const containerWidth = thumbnailContainerElement.current.clientWidth;
        const thumbnailWidth = thumbnailScrollElement.current.clientWidth / itemCount;
        const halfPoint = containerWidth / HALF - thumbnailWidth / HALF;
        const activeElementPosition = halfPoint - thumbnailWidth * index;
        const max = thumbnailScrollElement.current.clientWidth - thumbnailContainerElement.current.clientWidth;
        const targetPosition = Math.min(Math.max(activeElementPosition, -max), 0);

        thumbnailScrollElement.current.style.transform = `translateX(${targetPosition}px)`;

        if (index > 0) {
          setLoadAllImages(true);
        }
      }
    },
    [itemCount],
  );

  useEffect(() => {
    setActiveThumbnail(activeImageIndex);
    showThumbnailInView(activeImageIndex);
  }, [activeImageIndex, showThumbnailInView]);

  return (
    <div className={Classes.root}>
      <div ref={thumbnailContainerElement} className={Classes.cover}>
        <div ref={thumbnailScrollElement} className={Classes.scroll}>
          {videoUrl && (
            <button
              type="button"
              onClick={() => onClick(0)}
              aria-label={t('gallery.videoLabel')}
              aria-current={activeThumbnail === 0 ? 'true' : undefined}
              className={`${Classes.item} ${activeThumbnail === 0 ? Classes.active : ''}`}
            >
              <video aria-hidden="true" className={Classes.video} muted src={videoUrl} />
              <span className={Classes.videoOverlay}>
                <Icon className={Classes.videoIcon} name="player-play" color="text-light" />
              </span>
            </button>
          )}
          {imageUrls.map((imageUrl, index) => {
            const itemIndex = index + videoOffset;
            const className = [
              Classes.item,
              itemIndex === activeThumbnail && Classes.active,
              imageFit === 'contain' && Classes.contain,
            ]
              .filter(Boolean)
              .join(' ');

            return (
              <button
                type="button"
                onClick={() => onClick(itemIndex)}
                aria-label={t('gallery.carousel.thumbnail', { index: itemIndex + 1, total: itemCount })}
                aria-current={itemIndex === activeThumbnail ? 'true' : undefined}
                key={index}
                className={className}
                style={
                  loadAllImages || itemIndex < maxVisibleImageCount
                    ? {
                        backgroundImage: `url("${getThumbnailImageUrl(imageUrl, imageSizes)}") `,
                      }
                    : {}
                }
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Thumbnails;
